import { FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import ErrorBox from 'components/ErrorBox'
import TextArea from 'components/GlobalStyles/Inputs/TextArea'
import TextInput from 'components/GlobalStyles/Inputs/TextInput'
import RoundedButton from 'components/RoundedButton'
import Spacer from 'components/Spacer'
import SuccessBox from 'components/SuccessBox'

import { buyerInquiry, contactUs } from 'services/contactUs'
import { contactSeller } from 'services/listings'

import { isProduction } from 'utils/nodeEnv'
import validateEmail from 'utils/validateEmail'

import * as T from 'types'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

interface ITextInputContainerProps {
  notInSidebar?: boolean
}
const TextInputContainer = styled.div<ITextInputContainerProps>`
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    display: flex;
    flex-direction: ${props => (props.notInSidebar ? 'row' : 'column')};
  }
`

const ButtonContainer = styled.div<ITextInputContainerProps>`
  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    width: ${props => (props.notInSidebar ? '240px;' : '100%')};
    margin: ${props => (props.notInSidebar ? '0 auto' : 'inherit')};
  }
`

const StyledHeader = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 25px;
  color: ${props => props.theme.colors.black};
`

const StyledSubheader = styled(StyledHeader)`
  font-size: 16px;
  line-height: 22px;
`

const StyledDisclaimer = styled.span`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  color: ${props => props.theme.colors.transparentBlackAlpha50};
`

const StyledLink = styled.a`
  color: ${props => props.theme.colors.transparentBlackAlpha50};
`

interface IProps {
  dismissAfter?: number
  formType?: 'contactUs' | 'contactSeller' | 'buyerInquiry'
  listingId?: string // for contactSeller inquiries
  messageHeading?: string
  messagePlaceholder?: string
  requirePhone: boolean
  submitText?: string
  notInSidebar?: boolean
  listingAddress?: string
}

const initialFieldsState: T.IContactSellerData = {
  name: '',
  email: '',
  phone: '',
  message: '',
}

interface IFieldsValidtyState {
  [key: string]: boolean
}

const initialFieldsValidityState: IFieldsValidtyState = {
  name: false,
  email: false,
  phone: false,
  message: false,
}

const ContactForm: FunctionComponent<IProps> = ({
  dismissAfter = 60000,
  formType,
  listingAddress = '',
  listingId = '',
  messageHeading = '',
  messagePlaceholder = 'Ask a Question...',
  requirePhone = false,
  submitText,
  notInSidebar = false,
}) => {
  const theme = useContext(ThemeContext)

  const [error, setError] = useState('')
  const [showChatMessage, setShowChatMessage] = useState(false)
  const [allFieldsValid, setAllFieldsValid] = useState(false)

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [messageSent, setMessageSent] = useState(false)

  const [fieldsValidity, setFieldsValidity] = useState(initialFieldsValidityState)

  const checkAllFieldsValidity = () => {
    const checkedKeys = Object.keys(fieldsValidity).filter(
      key => key !== 'phone' || (key === 'phone' && requirePhone),
    )

    const newAllFieldsValid = checkedKeys.every(key => !!fieldsValidity[key])
    setAllFieldsValid(newAllFieldsValid)
    if (newAllFieldsValid && error) {
      setError('')
    }
  }

  useEffect(() => {
    checkAllFieldsValidity()
  }, [fieldsValidity])

  const [formInfo, setFormInfo] = useState(initialFieldsState)

  const handleFormChange = (name: string, value: string) => {
    setFormInfo({ ...formInfo, [name]: value })
    if (name === 'email') {
      setFieldsValidity({
        ...fieldsValidity,
        email: !!value && validateEmail(value),
      })
    } else if (name === 'phone') {
      if (!requirePhone) return

      setFieldsValidity({
        ...fieldsValidity,
        [name]: !!value && value.length === 10 && value.indexOf('_') === -1,
      })
    } else {
      setFieldsValidity({
        ...fieldsValidity,
        [name]: !!value,
      })
    }
  }

  const apiAction = (params: T.IContactSellerData) => {
    switch (formType) {
      case 'contactUs':
        return contactUs(params)
      case 'contactSeller':
        return contactSeller(listingId, params)
      case 'buyerInquiry':
        return buyerInquiry(params)
      default:
        return Promise.resolve(false)
    }
  }

  const handleSubmit = async () => {
    setIsSubmitted(true)

    if (allFieldsValid) {
      let data
      let recaptchaToken = ''

      if (typeof window !== 'undefined' && window && (window as any).grecaptcha) {
        try {
          recaptchaToken = await (window as any).grecaptcha.enterprise.execute(
            `${process.env.GOOGLE_RECAPTCHA_KEY}`,
            { action: 'CONTACT_FORM' },
          )
        } catch (err) {
          // implicitly handled; will exit later due to !data
          if (!isProduction()) console.error('error handling recaptcha', err)
        }
      }

      if (recaptchaToken) {
        data = await apiAction({
          ...formInfo,
          address: listingAddress,
          token: recaptchaToken,
        })
      }

      if (!data) {
        setError('There was an error, please try again later')
        setShowChatMessage(true)
      } else {
        setMessageSent(true)
        setFormInfo({ ...initialFieldsState })
        setError('')
      }
    } else {
      setError('Some required fields were not filled in.')
    }
  }

  return (
    <Container>
      <TextInputContainer notInSidebar={notInSidebar}>
        <TextInput
          name="name"
          placeholder="Name"
          background={theme.colors.white}
          borderColor={theme.colors.grey3}
          onChange={handleFormChange}
          delay={0}
          value={formInfo.name}
          controlled
          submissionError={isSubmitted && !messageSent && !fieldsValidity.name}
        />
        <Spacer size={8} />
        <TextInput
          name="email"
          placeholder="Email"
          background={theme.colors.white}
          borderColor={theme.colors.grey3}
          onChange={handleFormChange}
          delay={0}
          value={formInfo.email}
          controlled
          submissionError={isSubmitted && !messageSent && !fieldsValidity.email}
        />
      </TextInputContainer>
      {requirePhone && (
        <>
          <Spacer size={8} />
          <TextInput
            name="phone"
            placeholder="Phone"
            background={theme.colors.white}
            borderColor={theme.colors.grey3}
            onChange={handleFormChange}
            delay={0}
            value={formInfo.phone}
            controlled
            submissionError={isSubmitted && !messageSent && !fieldsValidity.phone}
            isPhoneNumber
          />
        </>
      )}
      {messageHeading && (
        <>
          <Spacer size={30} />
          <StyledSubheader>{`${messageHeading}:`}</StyledSubheader>
        </>
      )}
      <Spacer size={8} />
      <TextArea
        name="message"
        placeholder={messagePlaceholder}
        background={theme.colors.white}
        height={120}
        onChange={handleFormChange}
        delay={0}
        defaultValue={formInfo.message}
        submissionError={isSubmitted && !messageSent && !fieldsValidity.message}
        showCharsRemainingOutside
      />
      <Spacer size={30} />
      <StyledDisclaimer>
        {'This site is protected by reCAPTCHA and the Google '}
        <StyledLink href="https://policies.google.com/privacy">Privacy Policy</StyledLink>
        {' and '}
        <StyledLink href="https://policies.google.com/terms">Terms of Service</StyledLink>
        {' apply.'}
      </StyledDisclaimer>
      <Spacer size={30} />
      {!messageSent
        ? (
          <ButtonContainer notInSidebar={notInSidebar}>
            <RoundedButton
              text={submitText}
              background={theme.colors.blue4}
              color={theme.colors.white}
              radius={20}
              onClick={handleSubmit}
              noUppercase
              fullWidth
            />
          </ButtonContainer>
        )
        : (
          <SuccessBox
            message="Message Sent"
            dismissAfter={dismissAfter}
          />
        )}
      <Spacer size={10} />
      {error !== '' && (
        <ErrorBox
          error={error}
          showChatMessage={showChatMessage}
        />
      )}
    </Container>
  )
}

export default ContactForm
